import { LineChart, Line, BarChart, Bar, ReferenceLine, Tooltip, ResponsiveContainer } from 'recharts';
import { dropLeadingZeroDays } from '../lib/timeSeries.js';

export type MetricStatus = 'green' | 'yellow' | 'red';

export interface SparklinePoint {
  date: string;
  value: number | null;
}

interface MetricSparklineProps {
  data: SparklinePoint[];
  kind: 'line' | 'bar';
  // Optional — metrics without a baseline comparison (e.g. TTV) still
  // surface a trendline, just rendered in a neutral color.
  status?: MetricStatus;
  baseline?: number;
  ariaLabel?: string;
}

const STATUS_COLOR: Record<MetricStatus, string> = {
  green: 'var(--loop-green-600)',
  yellow: 'var(--loop-yellow-800)',
  red: 'var(--loop-red-600)',
};

const NEUTRAL_STROKE = 'var(--loop-neutral-600)';

export function isZeroPoint(p: SparklinePoint): boolean {
  return p.value === null || p.value === 0;
}

// Pure selector: when there's no signal we render the full untrimmed series
// so the chart's X-axis spans the full window (empty state still looks like
// a chart). When there IS signal we use the trimmed series so leading zeros
// don't push the data to the right edge.
export function selectSparklineSeries(
  data: SparklinePoint[],
): { series: SparklinePoint[]; hasSignal: boolean } {
  const trimmed = dropLeadingZeroDays(data, isZeroPoint);
  const hasSignal = trimmed.some((p) => !isZeroPoint(p));
  return { series: hasSignal ? trimmed : data, hasSignal };
}

function fmtTooltipValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return String(v);
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function MetricSparkline({ data, kind, status, baseline, ariaLabel }: MetricSparklineProps) {
  const { series: seriesToRender, hasSignal } = selectSparklineSeries(data);
  const color = status ? STATUS_COLOR[status] : NEUTRAL_STROKE;
  // Accessible name fallback + "no data yet" suffix so screen readers get
  // both the metric identity AND the empty-state hint (the overlay span
  // alone isn't reliably announced because the parent has role="img").
  const baseLabel = ariaLabel ?? 'Metric trend';
  const composedAriaLabel = hasSignal ? baseLabel : `${baseLabel} (no data yet)`;

  return (
    <div role="img" aria-label={composedAriaLabel} style={{ height: 32, width: '100%', position: 'relative' }}>
      <ResponsiveContainer width="100%" height="100%">
        {kind === 'bar' ? (
          <BarChart data={seriesToRender} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <Tooltip
              formatter={(v) => fmtTooltipValue(v)}
              labelStyle={{ color: 'var(--loop-neutral-1100)' }}
              contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
            />
            <ReferenceLine y={0} stroke="var(--loop-neutral-300)" />
            <Bar dataKey="value" fill={color} isAnimationActive={false} />
          </BarChart>
        ) : (
          <LineChart data={seriesToRender} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <Tooltip
              formatter={(v) => fmtTooltipValue(v)}
              labelStyle={{ color: 'var(--loop-neutral-1100)' }}
              contentStyle={{ fontSize: '0.75rem', padding: '4px 8px' }}
            />
            <ReferenceLine y={0} stroke="var(--loop-neutral-300)" />
            {baseline !== undefined && (
              <ReferenceLine y={baseline} stroke="var(--loop-neutral-500)" strokeDasharray="3 3" />
            )}
            <Line
              dataKey="value"
              stroke={color}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
      {!hasSignal && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--loop-neutral-500)',
            fontSize: '0.7rem',
            fontStyle: 'italic',
            pointerEvents: 'none',
          }}
        >
          no data yet
        </span>
      )}
    </div>
  );
}
